import { faChevronRight, faFlag } from "@fortawesome/duotone-light-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  ChildUser,
  FlexBox,
  JoonColorExpanded,
  JoonUIColor,
  SPACING,
  Typography,
} from "@joonapp/web-shared"
import { useState } from "react"
import { Trans, useTranslation } from "react-i18next"

import { useFlaggedDoterChatLogsQuery } from "../../networking/chatSessions/queries"
import { useFamilyQuery } from "../../networking/queries"
import {
  AIChatFlaggedPromptNotification,
  ChildTabNavOption,
  FlagType,
} from "../../types"
import { useRouter } from "../../util/router"
import { getUserFromFamily } from "../../util/util"
import { useDoterChatStore } from "../family/childUser/useDoterChatStore"
import useChildTabNavStore from "../family/hooks/useChildTabNavStore"
import useFamilyUserSelectStore from "../family/hooks/useFamilyUserSelect"
import DoterChatFlag from "../family/manageGameplay/doterChat/DoterChatFlag"

const DoterAIAlertsSection = () => {
  const { t } = useTranslation()
  const { data: redFlags, isLoading: isLoadingRedFlags } =
    useFlaggedDoterChatLogsQuery()
  const { data: greenFlags, isLoading: isLoadingGreenFlags } =
    useFlaggedDoterChatLogsQuery(FlagType.GREEN)

  const unreadRedFlags = redFlags?.filter((convo) => !convo.date_read)
  const unreadGreenFlags = greenFlags?.filter((convo) => !convo.date_read)

  if (
    isLoadingRedFlags ||
    isLoadingGreenFlags ||
    !unreadRedFlags ||
    !unreadGreenFlags ||
    (unreadRedFlags.length === 0 && unreadGreenFlags.length === 0)
  )
    return null

  const childUserIds = [
    ...new Set(
      [...unreadRedFlags, ...unreadGreenFlags].map((convo) => convo.user_id)
    ),
  ]

  return (
    <FlexBox
      style={{
        padding: SPACING.space4,
        border: `1px solid ${JoonUIColor.semantic.alert}`,
        borderRadius: SPACING.space2,
        background: JoonUIColor.background.primaryNeutral,
      }}
      direction="column"
      gap={SPACING.space2}
    >
      <FlexBox direction="row" align="center" gap={SPACING.space2}>
        <FontAwesomeIcon
          icon={faFlag}
          fontSize={16}
          color={JoonUIColor.semantic.alert}
        />
        <Typography variant="bodyBold">
          {t("ME_DOTER_AI_ALERTS_TITLE")}
        </Typography>
      </FlexBox>
      <FlexBox direction="column" gap={SPACING.space2}>
        {childUserIds.map((childUserId) => {
          const redFlags = unreadRedFlags.filter(
            (convo) => convo.user_id === childUserId
          )
          const greenFlags = unreadGreenFlags.filter(
            (convo) => convo.user_id === childUserId
          )
          return (
            <DoterAIAlertItem
              key={childUserId}
              redFlags={redFlags}
              greenFlags={greenFlags}
              childUserId={childUserId}
            />
          )
        })}
      </FlexBox>
    </FlexBox>
  )
}

const DoterAIAlertItem = ({
  redFlags,
  greenFlags,
  childUserId,
}: {
  redFlags: AIChatFlaggedPromptNotification[]
  greenFlags: AIChatFlaggedPromptNotification[]
  childUserId: number
}) => {
  const [isHovered, setIsHovered] = useState(false)
  const { data: family } = useFamilyQuery()
  const { setSelectedUserId } = useFamilyUserSelectStore()
  const { setTab } = useDoterChatStore()
  const { setSelectedTab } = useChildTabNavStore()
  const child = getUserFromFamily(family!, childUserId || 0) as
    | ChildUser
    | undefined

  const router = useRouter()
  const redFlagTypes = [
    ...new Set(redFlags.flatMap((convo) => convo.message.prompt_flags)),
  ]
  const greenFlagTypes = [
    ...new Set(
      greenFlags.flatMap((convo) => convo.message.positive_flag_summary?.flags)
    ),
  ]
  const flagTypes = [...redFlagTypes, ...greenFlagTypes]

  const handleClick = () => {
    router.push("/family")
    setSelectedUserId(childUserId)
    setSelectedTab(ChildTabNavOption.DOTER_CHATS)
    setTab(redFlags.length > 0 ? FlagType.RED : FlagType.GREEN)
  }

  return (
    <button onClick={handleClick} style={{ width: "100%" }}>
      <FlexBox
        style={{
          border: `1px solid ${JoonUIColor.border.default}`,
          borderRadius: SPACING.space2,
          overflow: "hidden",
          transition: "border-color 0.2s ease-in-out",
          borderColor: isHovered
            ? JoonColorExpanded.neutral300
            : JoonUIColor.border.default,
        }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <FlexBox
          direction="row"
          align="center"
          wrap={false}
          gap={SPACING.space2}
          style={{
            padding: `${SPACING.space2} ${SPACING.space3}`,
            background: JoonUIColor.background.lightGray,
          }}
          justify="space-between"
        >
          <Typography variant="bodySmall">
            <Trans
              i18nKey="ME_DOTER_AI_REVIEW"
              values={{ name: child?.name }}
              components={{
                b: <Typography variant="bodySmallBold">{""}</Typography>,
              }}
            />
          </Typography>
          <FontAwesomeIcon
            icon={faChevronRight}
            fontSize={16}
            color={JoonUIColor.icon.neutral}
          />
        </FlexBox>
        <FlexBox
          direction="row"
          gap={SPACING.space1}
          style={{
            padding: SPACING.space3,
            background: JoonUIColor.background.primaryNeutral,
          }}
        >
          {flagTypes.map(
            (flagType) =>
              flagType && <DoterChatFlag key={flagType} type={flagType} />
          )}
        </FlexBox>
      </FlexBox>
    </button>
  )
}

export default DoterAIAlertsSection
